import delay from "delay";
import { default as getConfig } from "../config";
import { ACTIVITY_STORAGE_KEY, StatementOfTaxability, Taxable } from "./constants";

const { appEnv, hostnames, apiVersion3, apiVersion, assetsPath } = getConfig();

const { ecmApiHost } = hostnames;

export const urlToPageMap = {
    "search-certificate": "/search/certificates",
    "search-pending": "/search/pending",
    "search-customer": "/search/customers",
    "search-vendor-certificates": "/search/vendor-certificates",
    review: "/review",
    overview: "/overview",
    retail: "/retail",
    retailSearch: "search",
    retailCustomer: "customer/:id?",
    retailCustomerAndCertificates: "customer-certificates/:id",
    retailAddCertificate: "certificate",
    settings: "/settings",
    customerDetail: "customer/:id?",
};

export function updateSelectedAttribute(array, value) {
    array?.map(x => {
        const arrayValue = x;
        if (arrayValue?.value?.toString()?.toLowerCase() === value?.toString()?.toLowerCase())
            arrayValue.selected = true;
        else arrayValue.selected = false;
        return arrayValue;
    });
}

export const toBase64 = file =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });

export function isNumeric(value) {
    return /^-?\d+$/.test(value);
}

export function getFormattedDate(dateString, USFormat) {
    if (dateString === "1970-01-01T00:00:00") {
        return null;
    }

    const date = new Date(dateString);
    const year = date.getFullYear();

    let month = (1 + date.getMonth()).toString();
    month = month.length > 1 ? month : `0${month}`;

    let day = date.getDate().toString();
    day = day.length > 1 ? day : `0${day}`;

    if (USFormat) return `${year}-${month}-${day}`;

    return `${month}/${day}/${year}`;
}

export function getURLConstantsKey(pathname) {
    const urlRegex = /\/(customer|certificate|customer-exemptions|campaigns|review)\/(\d+)/;
    return pathname?.match(urlRegex);
}

export function validateEmail(email) {
    const regEx =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
    return !!email?.match(regEx);
}

export function buildURL(baseURL) {
    const url = new URL(appEnv === "local" ? `http://${baseURL}` : `https://${baseURL}`);
    return url.href;
}

export function buildURLWithQuery(baseURL, filter, include, top, skip, sortColumn) {
    const url = new URL(appEnv === "local" ? `http://${baseURL}` : `https://${baseURL}`);
    if (filter) {
        url.searchParams.append("$filter", filter);
    }
    if (include && include.length) {
        url.searchParams.append("$include", include.join());
    }
    if (top) {
        url.searchParams.append("$top", top);
    } else if (top !== 0) {
        const rowsPerPage = JSON.parse(localStorage.getItem("rowsPerPage"));
        if (rowsPerPage) {
            url.searchParams.append("$top", rowsPerPage);
        } else {
            url.searchParams.append("$top", 20);
        }
    }
    if (skip) {
        url.searchParams.append("$skip", skip);
    }
    if (baseURL.includes("/v3/") && sortColumn) {
        url.searchParams.append("$orderBy", sortColumn);
    } else if (sortColumn) {
        let [orderByParam, order] = sortColumn;
        order = order ? "asc" : "desc";
        orderByParam = `${orderByParam} ${order}`;
        url.searchParams.append("$orderBy", orderByParam);
    }
    return url.href;
}

export function buildApiV2Point5UrlWithQuery(relativeUrl, filter, include, top, skip, sortColumn) {
    return buildURLWithQuery(
        `//${ecmApiHost}/api/${apiVersion}/${relativeUrl}`,
        filter,
        include,
        top,
        skip,
        sortColumn
    );
}

export function buildApiV2Point5Url(relativeUrl) {
    return buildURL(`//${ecmApiHost}/api/${apiVersion}/${relativeUrl}`);
}

export function buildApiV3UrlWithQuery(relativeUrl, filter, include, top, skip, sortColumn) {
    return buildURLWithQuery(
        `//${ecmApiHost}/api/${apiVersion3}/${relativeUrl}`,
        filter,
        include,
        top,
        skip,
        sortColumn
    );
}

export function buildApiV3Url(relativeUrl) {
    return buildURL(`//${ecmApiHost}/api/${apiVersion3}/${relativeUrl}`);
}

export function poll(fn, retries = Infinity, timeoutBetweenAttempts = 1000) {
    return Promise.resolve()
        .then(fn)
        .catch(function retry(err) {
            if (retries - 1 > 0) return delay(timeoutBetweenAttempts).then(fn).catch(retry);
            throw err;
        });
}

export function toTitleCase(str) {
    return str?.replace(/\w\S*/g, txt => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

export function fixSearchStringForPostgres(str) {
    let searchString = str?.replace(/&/g, "%26");
    searchString = searchString?.replace(/#/g, "%23");
    return searchString?.replace(/'/g, "''");
}

export function fixSearchStringForCertificates(str) {
    return str.replace(/'/g, "''");
}

export function sanitizeAddressForDisplay(address) {
    if (!address) return "";

    return address
        .replace(/[\n\r\t\f\v]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

export function checkSelectComponentProps(component, props) {
    if (props.onAdd === undefined) {
        throw Error(`You must pass an onAdd function to ${component}`);
    }
    if (props.onRemove === undefined) {
        throw Error(`You must pass an onRemove function to ${component}`);
    }
}

export function sentenceCase(text, ignoreAndReturnEmpty = []) {
    if (text == null) {
        return "";
    }

    if (ignoreAndReturnEmpty?.some(value => text?.includes(value))) {
        return "";
    }

    const textWithCase = text
        ?.toLowerCase()
        ?.split(" ")
        ?.map(word => {
            let char = "";
            if (word?.includes("/")) {
                char = "/";
            }
            if (word?.includes("-")) {
                char = "-";
            }
            return char
                ? word.charAt(0).toUpperCase() +
                      word.slice(1, 1 + word.search(char)) +
                      word.charAt(1 + word.search(char)).toUpperCase() +
                      word.slice(2 + word.search(char))
                : word.charAt(0).toUpperCase() + word.slice(1);
        });
    return textWithCase?.join(" ");
}

export function nextDays(days) {
    const today = new Date();
    return new Date(today.setDate(today.getDate() + days)).toISOString().slice(0, 10);
}

export function getTimeFrame(timeLength) {
    const timeFrames = timeLength?.split(" ");
    const [timeDuration, timeFrame] = timeFrames;
    const isMonth = timeFrame?.match(/months/) != null;
    if (timeLength?.match(/months|days/) == null) {
        return { timeFrame: timeDuration, timeDuration: timeFrame, isMonth };
    }
    return { timeFrame, timeDuration, isMonth };
}

export function getDuration(timeLength) {
    const timeFrames = timeLength?.split(" ");
    return timeFrames?.length > 0 ? timeFrames[0] : null;
}

export function previousDays(days) {
    const today = new Date();
    return new Date(today.setDate(today.getDate() - days)).toISOString().slice(0, 10);
}

export function getdefaultValue(value) {
    switch (typeof value) {
        case "number":
            return 0;
        case "string":
            return "";
        case "boolean":
            return false;
        case "object":
            return [];
        default:
            return null;
    }
}

export function formatTemplate(content) {
    let formattedContent = content?.replaceAll("â", "&apos;");

    if (!formattedContent) return formattedContent;

    // Apply same logic as getHtmlcontent - PDF-optimized spacing
    // PRESERVE existing styles that include manual indentation (&nbsp;)
    formattedContent = formattedContent.replace(/<p([^>]*)>/g, (match, attributes) => {
        if (match.includes("{{COMPANY_LOGO}}")) {
            return `<p style="text-align:right; margin: 0; padding: 0; line-height: 1.2;">`;
        }

        // If paragraph already has proper styling (manual indentation), keep it
        if (
            attributes.includes("line-height") &&
            (attributes.includes("margin-bottom") || attributes.includes("margin:"))
        ) {
            return match; // Preserve existing manual indentation styles!
        }

        // Apply PDF-optimized spacing - use margin: 0 and smaller line-height for PDF compatibility
        const style = "margin: 0; padding: 0; line-height: 1.2;";
        const cleanAttributes = attributes
            .replace(/class="[^"]*"/gi, "")
            .replace(/style="[^"]*"/gi, "");
        return `<p${cleanAttributes} style="${style}">`;
    });

    // Remove empty paragraphs
    formattedContent = formattedContent.replace(/<p[^>]*>\s*<\/p>/g, "");

    return formattedContent;
}

export function matchSubscriptions(subscriptions, productInfo) {
    const match = subscriptions?.filter(sub => productInfo?.includes(sub));
    return match?.length > 0;
}

export function matchRole(allowedRoles, currentRole) {
    return allowedRoles?.some(item => currentRole?.includes(item));
}

export const listDifference = (list1, list2, returnIntersect = false) =>
    list1?.filter(
        (
            set => a =>
                returnIntersect === set?.has(a.id)
        )(new Set(list2?.map(b => b.id)))
    );

export const retainCriticalData = e => {
    let vendorColumnConfigData = null;

    if (localStorage.getItem("vendorColumnConfig")) {
        vendorColumnConfigData = JSON.parse(localStorage.getItem("vendorColumnConfig"));
    }
    localStorage.clear();

    if (vendorColumnConfigData)
        localStorage.setItem("vendorColumnConfig", JSON.stringify(vendorColumnConfigData));

    if (e?.detail?.url) {
        window.location = e.detail.url;
    }
};

export const clearLocalStorage = () => {
    if (localStorage.getItem(ACTIVITY_STORAGE_KEY)) {
        localStorage.removeItem(ACTIVITY_STORAGE_KEY);
    }
};

export const errorTag = "error";

export const noErrorTag = "no-error";

export function formatDateWithHyphen(date, includeTime = true, separator = "-") {
    return new Date(date)
        .toLocaleString("en-US", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            ...(includeTime && {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            }),
        })
        .replace(/\//g, separator);
}

export const formatDate = ({ date, format = "en-US", includeTime = true, separator = "-" }) => {
    return new Date(date)
        .toLocaleString(format, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            ...(includeTime && {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            }),
        })
        .replace(/\//g, separator);
};

export function removeSpaces(str) {
    return str?.replace(/ /g, "");
}

export function lowerCaseAllExceptFirst(str) {
    return `${str[0].toUpperCase()}${str.slice(1).toLowerCase()}`;
}

export function convertFieldToString(array, field, nestedObjects) {
    if (!Array.isArray(array)) return [];
    return array.map(element => {
        const copyElement = { ...element };
        if (field && copyElement[field]) copyElement[field] = copyElement[field]?.toString();
        for (let i = 0; i < nestedObjects?.length; i += 1) {
            if (copyElement[nestedObjects[i]]?.id)
                copyElement[nestedObjects[i]].id = copyElement[nestedObjects[i]]?.id?.toString();
        }
        return copyElement;
    });
}
// Disable any hyperlink programmatically
export function disableLink(id) {
    const element = document.getElementById(id);
    if (!element.hasClickListener) {
        element.addEventListener("click", event => event.preventDefault());
        element.hasClickListener = true;
    }
}

export const getLabelForCustomField = (type, element) => {
    let fieldLabel = element;
    if (type === "boolean") {
        if (element.toUpperCase() === "Y") {
            fieldLabel = "Yes";
        } else {
            fieldLabel = "No";
        }
    }

    return fieldLabel;
};

export const isOneWebDomainRuntime = () => {
    return !!window.AvaAppConfig;
};

// function is used to transform the exemption reason name to 'Statement of taxability' instead of 'taxable'
// for matrix, drop down and grid data
export function transformExemptionData(data, isMatrix, isDropdown, keys = ["name"]) {
    const transformTaxableName = (item, key) => {
        if (item[key]?.toLowerCase() === Taxable) {
            return {
                ...item,
                [key]: StatementOfTaxability,
            };
        }
        return item;
    };
    if (isMatrix === true && isDropdown === false) {
        if (Array.isArray(data)) {
            return data.map(item => {
                // Handle items in the exemption matrix
                return item.exemptReason
                    ? {
                          ...item,
                          exemptReason: transformTaxableName(item.exemptReason, "name"),
                      }
                    : item;
            });
        }
        // Handle the single object case with expectedTaxCodeName and taxCodeName
        return {
            ...data,
            expectedTaxCodeName: transformTaxableName(data, "expectedTaxCodeName")
                .expectedTaxCodeName,
            taxCodeName: transformTaxableName(data, "taxCodeName").taxCodeName,
        };
    }
    if (isMatrix === false && isDropdown === true) {
        return data.map(item => {
            return transformTaxableName(item, "name");
        });
    }

    const deepCopy = obj => JSON.parse(JSON.stringify(obj));

    const updatedData = deepCopy(data);

    const valueArray = Array.isArray(updatedData.data?.value) ? updatedData.data.value : [];

    updatedData.data.value = valueArray.map(record => {
        let updatedRecord = { ...record };
        keys.forEach(key => {
            updatedRecord = transformTaxableName(updatedRecord, key);
        });
        return updatedRecord;
    });

    return updatedData;
}

export const getRowsPerPage = (paginateData, rowsPerPage) => {
    let rowsToReturn = rowsPerPage;
    if (paginateData.totalRecords <= 50 && rowsPerPage === 100) {
        rowsToReturn = 50;
    } else if (paginateData.totalRecords <= 20 && rowsPerPage >= 50) {
        rowsToReturn = 20;
    } else if (paginateData.totalRecords <= 10 && rowsPerPage >= 20) {
        rowsToReturn = 10;
    }

    return rowsToReturn;
};

export const groupCommunicationTaxTypesByExposureZoneId = (taxTypes, exposureZoneIds) => {
    const groupedTaxTypes = {};
    taxTypes.forEach(taxType => {
        const { exposureZoneId } = taxType;
        if (exposureZoneIds.includes(exposureZoneId)) {
            if (!groupedTaxTypes[exposureZoneId]) {
                groupedTaxTypes[exposureZoneId] = [];
            }
            groupedTaxTypes[exposureZoneId].push(taxType.id);
        }
    });
    return groupedTaxTypes;
};

export const getCustomValue = (customField, fieldName) => {
    // Input validation
    if (!customField || !fieldName) {
        return [];
    }

    // Remove duplicates
    const uniqueFields = [...new Set(customField)].filter(
        field => field !== null && field !== "null"
    );

    if (uniqueFields.length === 0) {
        return [];
    }

    // Find matching field and extract value
    const FIELD_SEPARATOR = ":";

    const matchingField = uniqueFields.find(field =>
        field.match(new RegExp(`^\\s*${RegExp.escape(fieldName)}\\s*${FIELD_SEPARATOR}`))
    );

    if (matchingField) {
        const [, value] = matchingField.split(FIELD_SEPARATOR);
        return value.trim();
    }

    return "";
};

export const getAssetURL = asset =>
    assetsPath ? `${assetsPath}/assets/${asset}` : `/assets/${asset}`;

const replaceCustomFieldKeyWithId = (list, obj) => {
    if (!obj) return obj;
    const result = {};

    Object.keys(obj).forEach(key => {
        const item = list.find(item => item.fieldName.toLowerCase() === key.toLowerCase());
        const newKey = item ? item.id : null;
        if (!newKey) return;
        result[newKey] = obj[key];
    });

    return result;
};

const replaceValueWithId = (list, value = []) => {
    const ids = value
        .map(_value => {
            return list.find(item => item.name.toLowerCase() === _value.toLowerCase())?.id;
        })
        .filter(Boolean);

    return ids;
};

const arrayToString = array => {
    if (!Array.isArray(array)) return "";

    return array
        .filter(Boolean) // Remove any falsy values
        .join("\n"); // Join with newline character
};

export const parseFiltersState = (filters, data, initialState = {}) => {
    let updatedFilters = { ...filters };

    // Step 1: Extract values from nested objects (e.g., {value: ["California"]} -> ["California"])
    const extractedFilters = {};
    Object.entries(updatedFilters).forEach(([key, value]) => {
        if (
            value &&
            typeof value === "object" &&
            Object.prototype.hasOwnProperty.call(value, "value")
        ) {
            extractedFilters[key] = value.value;
        } else {
            extractedFilters[key] = value;
        }
    });

    // Step 2: Apply existing logic for custom fields and value replacement
    const customFieldKey = Object.keys(extractedFilters).find(key => key.includes("CustomFields"));

    if (customFieldKey && data.customFields) {
        extractedFilters[customFieldKey] = replaceCustomFieldKeyWithId(
            data.customFields.list,
            extractedFilters[customFieldKey]
        );
    }

    Object.entries(data).forEach(([key, value]) => {
        if (value && value.list) {
            extractedFilters[key] = replaceValueWithId(value.list, extractedFilters[key]);
            if (
                ["excludeCustomerLabels", "certificatesLabels", "customersLabels"]?.includes(key) &&
                extractedFilters[key].length > 0
            ) {
                // Convert string values to numbers and add them to the array
                const numericValues = extractedFilters[key]
                    .map(value => {
                        const numValue = Number(value);
                        return !isNaN(numValue) ? numValue : null;
                    })
                    .filter(value => value !== null);

                extractedFilters[key] = [...extractedFilters[key], ...numericValues];
            }
        }
    });

    Object.entries(extractedFilters).forEach(([key, value]) => {
        if (key.toLowerCase().includes("date")) {
            extractedFilters[key] = value?.split?.("T")?.[0] || value;
        }
    });

    extractedFilters["certificateIds"] = arrayToString(extractedFilters["certificateIds"]);
    extractedFilters["customerCodes"] = arrayToString(extractedFilters["customerCodes"]);

    // Step 3: Merge with initial state - add missing keys from initial state
    return { ...initialState, ...extractedFilters };
};
// Helper function to get and parse localStorage items
export const getStoredItem = key => {
    const stored = localStorage.getItem(key);
    if (stored) {
        try {
            return JSON.parse(stored);
        } catch {
            return null;
        }
    }
    return null;
};

// Helper function to set and stringify localStorage items
export const setStoredItem = (key, value) => {
    try {
        const stringifiedValue = JSON.stringify(value);
        localStorage.setItem(key, stringifiedValue);
        return true;
    } catch {
        return false;
    }
};

export const isUndefinedOrNull = obj => {
    return typeof obj === "undefined" || obj === null;
};

export const isNullOrEmptyObject = obj => {
    return isUndefinedOrNull(obj) || Object.keys(obj).length === 0;
};

export const isNotNullOrEmptyObject = obj => {
    return !isNullOrEmptyObject(obj);
};

export const isNullOrEmptyArray = arr => {
    return isUndefinedOrNull(arr) || arr.length === 0;
};

export const isNotNullOrEmptyArray = arr => {
    return !isNullOrEmptyArray(arr);
};

export const sanitizeUrl = url => {
    try {
        const parsed = new URL(url);
        return ["http:", "https:"].includes(parsed.protocol) ? url : "#";
    } catch {
        return "#";
    }
};

export const downloadFileFromStream = ({ response, fileName }) => {
    if (response.headers["content-type"].includes("application/json")) {
        return;
    }
    const blob = new Blob([response.data], { type: response.data.type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const truncateString = (str, maxLength) => {
    if (str.length <= maxLength) {
        return str;
    }
    return str.slice(0, maxLength) + "...";
};
